import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  applyReadReceipt,
  applyMessageUpdate,
  fetchInbox,
  fetchThread,
  receiveBroadcastMessage,
  setThreadTyping,
} from '../../../store/slices/messengerSlice';
import messengerSocket from '../../../services/messengerSocket';

// WS is the primary push channel. Polling stays in place as a safety net at
// a much slower cadence whenever the socket is connected — drops back to the
// original tight cadence the moment WS disconnects so chat never silently
// stalls if the WS layer has an outage.
const INBOX_INTERVAL_MS = 5000;
const THREAD_INTERVAL_MS = 1500;
const INBOX_INTERVAL_MS_WS = 30000;
const THREAD_INTERVAL_MS_WS = 30000;

export function useMessengerPolling({ tab, activeSessionId, currentUserId, enabled = true }) {
  const dispatch = useDispatch();
  const inboxTimer = useRef(null);
  const threadTimer = useRef(null);
  const threadInFlight = useRef(false);
  const wsConnectedRef = useRef(messengerSocket.connected);

  // The pinned GeeVee assistant is a virtual conversation (sentinel id, no DB
  // row). Never fetch/subscribe a real thread for it — that would 404 the API
  // and subscribe the socket to a nonexistent session.
  const threadSessionId =
    activeSessionId && activeSessionId !== 'geevee' ? activeSessionId : null;

  // Open the singleton WS once. Subscribe to / unsubscribe from threads as
  // the active session changes. New-message pushes trigger an immediate
  // refetch — keeps the existing Redux state machine as the source of truth.
  useEffect(() => {
    if (!enabled) return undefined;
    messengerSocket.connect();
    const offConnected = messengerSocket.on('connected', () => {
      wsConnectedRef.current = true;
      restartIntervals();
    });
    const offDisconnected = messengerSocket.on('disconnected', () => {
      wsConnectedRef.current = false;
      restartIntervals();
    });
    const offMessage = messengerSocket.on('message.new', (evt) => {
      // Push the broadcast directly into the thread (deduped, replaces any
      // pending optimistic placeholder). Do NOT refetch the thread here —
      // that races with sendMessage.fulfilled and was causing the agent's
      // own reply to flicker out of the bubble list.
      if (evt.message) {
        dispatch(receiveBroadcastMessage({ sessionId: evt.session_id, message: evt.message }));
      }
      // Inbox preview/order still needs to update.
      dispatch(fetchInbox({ tab }));
    });
    const offMessageUpdated = messengerSocket.on('message.updated', (evt) => {
      // An existing bubble was edited or deleted elsewhere — replace it in
      // place. No inbox refetch needed unless it was the last message; the
      // cheap inbox poll cadence will catch the preview soon enough.
      if (evt.message) {
        dispatch(applyMessageUpdate({ sessionId: evt.session_id, message: evt.message }));
      }
    });
    const offTyping = messengerSocket.on('typing', (evt) => {
      dispatch(setThreadTyping({
        sessionId: evt.session_id,
        typing: evt.typing
          ? { user_id: evt.user_id, role: evt.role, name: evt.name }
          : null,
      }));
    });
    const offInbox = messengerSocket.on('inbox.unread', () => {
      dispatch(fetchInbox({ tab }));
    });
    const offRead = messengerSocket.on('read', (evt) => {
      if (!evt?.session_id || !evt?.up_to_message_id) return;
      // Ignore our own read events echoed back — only the peer reading our
      // outgoing bubbles should flip the tick.
      if (currentUserId && evt.by_user_id === currentUserId) return;
      dispatch(applyReadReceipt({
        sessionId: evt.session_id,
        upToMessageId: evt.up_to_message_id,
        readAt: evt.read_at,
      }));
    });
    return () => {
      offConnected();
      offDisconnected();
      offMessage();
      offMessageUpdated();
      offTyping();
      offInbox();
      offRead();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tab, activeSessionId, currentUserId]);

  useEffect(() => {
    if (!threadSessionId) return undefined;
    messengerSocket.subscribe(threadSessionId);
    return () => messengerSocket.unsubscribe(threadSessionId);
  }, [threadSessionId]);

  const restartIntervals = () => {
    const inboxMs = wsConnectedRef.current ? INBOX_INTERVAL_MS_WS : INBOX_INTERVAL_MS;
    const threadMs = wsConnectedRef.current ? THREAD_INTERVAL_MS_WS : THREAD_INTERVAL_MS;
    if (inboxTimer.current) {
      clearInterval(inboxTimer.current);
      inboxTimer.current = setInterval(() => dispatch(fetchInbox({ tab })), inboxMs);
    }
    if (threadTimer.current && threadSessionId) {
      clearInterval(threadTimer.current);
      threadTimer.current = setInterval(() => {
        if (threadInFlight.current) return;
        threadInFlight.current = true;
        dispatch(fetchThread(threadSessionId)).finally(() => { threadInFlight.current = false; });
      }, threadMs);
    }
  };

  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => dispatch(fetchInbox({ tab }));
    tick();
    const initialMs = wsConnectedRef.current ? INBOX_INTERVAL_MS_WS : INBOX_INTERVAL_MS;
    inboxTimer.current = setInterval(tick, initialMs);
    return () => clearInterval(inboxTimer.current);
  }, [dispatch, tab, enabled]);

  useEffect(() => {
    if (!enabled || !threadSessionId) return undefined;
    const tick = () => {
      if (threadInFlight.current) return;
      threadInFlight.current = true;
      dispatch(fetchThread(threadSessionId)).finally(() => {
        threadInFlight.current = false;
      });
    };
    tick();
    const initialMs = wsConnectedRef.current ? THREAD_INTERVAL_MS_WS : THREAD_INTERVAL_MS;
    threadTimer.current = setInterval(tick, initialMs);
    return () => clearInterval(threadTimer.current);
  }, [dispatch, threadSessionId, enabled]);
}

export default useMessengerPolling;
